const ACCOUNT_LINK_LABELS = [
  { pattern: /\/orders(?:\?|$)/, label: 'Orders' },
  { pattern: /\/profile(?:\?|$)|\/addresses(?:\?|$)/, label: 'Profile' },
];

const STYLE_ID = 'phoenix-account-dialog-fix';
const DOCUMENT_STYLE_ID = 'phoenix-account-document-fix';
const OVERLAY_ID = 'phoenix-account-backdrop';

const injectDocumentStyle = () => {
  if (document.getElementById(DOCUMENT_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = DOCUMENT_STYLE_ID;
  style.textContent = `
    /* Phoenix launch fix: neutralize cached transparent header renders at runtime. */
    body:has(.header[transparent]) .content-for-layout > .shopify-section:first-child {
      margin-top: 0 !important;
    }

    #header-group > .header-section {
      position: sticky !important;
      top: 0 !important;
      z-index: 1000 !important;
      background: #fff !important;
    }

    #header-component,
    #header-component[transparent],
    .header[transparent] {
      position: relative !important;
      inset: auto !important;
      background: #fff !important;
      opacity: 1 !important;
      z-index: auto !important;
    }

    #header-component :is(.header__underlay-open, .header__underlay-closed, .header__row) {
      background: #fff !important;
    }

    /* Phoenix launch fix: lock the storefront while the native account panel is open. */
    html[phoenix-account-open],
    html[phoenix-account-open] body {
      overflow: hidden !important;
      overscroll-behavior: none !important;
    }

    #phoenix-account-backdrop {
      position: fixed;
      inset: 0;
      z-index: 1900;
      background: rgba(17, 17, 17, 0.28);
      opacity: 1;
    }
  `;
  document.head.append(style);
};

const applyHeaderLayeringFix = () => {
  injectDocumentStyle();

  const header = document.getElementById('header-component');
  const headerSection = document.querySelector('#header-group > .header-section');
  const firstSection = document.querySelector('.content-for-layout > .shopify-section:first-child');

  if (header) {
    header.removeAttribute('transparent');
    header.removeAttribute('data-transparent-color-scheme');
    header.style.background = '#fff';
    header.style.position = 'relative';
    header.style.inset = 'auto';
    header.style.opacity = '1';
  }

  if (headerSection) {
    headerSection.style.position = 'sticky';
    headerSection.style.top = '0';
    headerSection.style.zIndex = '1000';
    headerSection.style.background = '#fff';
  }

  if (firstSection) {
    firstSection.style.marginTop = '0';
  }

  document.body.style.setProperty('--transparent-header-offset-boolean', '0');
};

const closeAccountPanel = (account) => {
  const root = account.shadowRoot;
  if (!root) return;

  let openElement;
  try {
    openElement = root.querySelector('dialog[open], [popover]:popover-open, .dialog[open]');
  } catch (_error) {
    openElement = root.querySelector('dialog[open], .dialog[open]');
  }
  if (openElement?.close) openElement.close();
  if (openElement?.hidePopover) openElement.hidePopover();
};

const setAccountOpenState = (account, isOpen) => {
  injectDocumentStyle();
  document.documentElement.toggleAttribute('phoenix-account-open', isOpen);

  let overlay = document.getElementById(OVERLAY_ID);

  if (isOpen && !overlay) {
    overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.setAttribute('aria-hidden', 'true');
    overlay.addEventListener('click', () => closeAccountPanel(account));
    document.body.append(overlay);
  } else if (!isOpen && overlay) {
    overlay.remove();
  }
};

const isAccountPanelOpen = (root) => {
  try {
    return Boolean(root.querySelector('dialog[open], [popover]:popover-open, .dialog[open], [aria-modal="true"]'));
  } catch (_error) {
    return Boolean(root.querySelector('dialog[open], .dialog[open], [aria-modal="true"]'));
  }
};

const setLabel = (link, label) => {
  link.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      node.remove();
      return;
    }

    if (
      node.nodeType === Node.ELEMENT_NODE &&
      node.tagName.toLowerCase() !== 'svg' &&
      node.dataset.phoenixAccountLabel !== 'true'
    ) {
      node.remove();
    }
  });

  let labelElement = link.querySelector('[data-phoenix-account-label="true"]');

  if (!labelElement) {
    labelElement = document.createElement('span');
    labelElement.dataset.phoenixAccountLabel = 'true';
    link.append(labelElement);
  }

  labelElement.textContent = label;
};

const injectStyle = (root) => {
  if (root.getElementById(STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    /* Phoenix launch fix: the native account panel must be a fully opaque layer above the header. */
    :host {
      position: relative !important;
      z-index: 1200 !important;
    }

    .dialog,
    dialog,
    [popover],
    [role="dialog"],
    .panel,
    .menu {
      position: fixed !important;
      z-index: 2000 !important;
      background: #fff !important;
      background-color: #fff !important;
      opacity: 1 !important;
      color: #111 !important;
      isolation: isolate !important;
      box-shadow: 0 24px 70px rgba(17, 17, 17, 0.24) !important;
    }

    .dialog *,
    dialog *,
    [popover] *,
    [role="dialog"] * {
      opacity: 1 !important;
    }

    .dialog::backdrop,
    dialog::backdrop,
    [popover]::backdrop {
      position: fixed !important;
      inset: 0 !important;
      z-index: 1990 !important;
      background: rgba(17, 17, 17, 0.28) !important;
      opacity: 1 !important;
      backdrop-filter: none !important;
    }

    @media (min-width: 751px) {
      .dialog {
        width: min(320px, calc(100vw - 48px)) !important;
        max-width: min(320px, calc(100vw - 48px)) !important;
        top: calc(var(--header-height, 72px) + 16px) !important;
        inset-inline-end: 32px !important;
      }
    }

    @media (max-width: 750px) {
      .dialog,
      dialog,
      [popover] {
        width: min(100vw - 24px, 420px) !important;
        max-width: calc(100vw - 24px) !important;
        top: calc(var(--header-height, 64px) + 12px) !important;
        inset-inline: 12px !important;
      }
    }
  `;
  root.append(style);
};

const syncAccount = (account) => {
  const root = account.shadowRoot;
  if (!root) return;

  injectStyle(root);
  setAccountOpenState(account, isAccountPanelOpen(root));

  root.querySelectorAll('a').forEach((link) => {
    const href = link.getAttribute('href') || '';
    const match = ACCOUNT_LINK_LABELS.find((item) => item.pattern.test(href));

    if (match) {
      setLabel(link, match.label);
    }
  });
};

const connectAccount = (account) => {
  if (account.__phoenixAccountExternalFix) return;

  account.__phoenixAccountExternalFix = true;

  const sync = () => syncAccount(account);

  account.addEventListener('open', () => {
    [0, 100, 300, 700, 1200, 2200, 4000].forEach((delay) => setTimeout(sync, delay));
  });

  account.addEventListener('close', () => setAccountOpenState(account, false));

  new MutationObserver(sync).observe(account, { childList: true, subtree: true });

  [0, 250, 1000, 2500].forEach((delay) => setTimeout(sync, delay));
};

const connectAccounts = () => {
  applyHeaderLayeringFix();
  document.querySelectorAll('shopify-account').forEach(connectAccount);
};

applyHeaderLayeringFix();
window.addEventListener('pageshow', applyHeaderLayeringFix);
window.addEventListener('resize', applyHeaderLayeringFix);

customElements.whenDefined('shopify-account').then(() => {
  connectAccounts();
  new MutationObserver(connectAccounts).observe(document.documentElement, { childList: true, subtree: true });
});
